import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { CircularProgress, FormControl, FormHelperText, Stack, TextField, Typography } from '@mui/material';
import { PhoneInputField, StepWrapper, StepperControls, StyledCardSection } from '@fnol/library/src/components';
import {
    ScreenProps,
    common,
    GenericObject,
    nameMaxLength,
    surnameMaxLength,
    getIsSalesForce,
    getApplicationId,
    questionMaxLength,
    hasValueAndIsSalesForce,
    checkIfSerbianMobPhoneNumber,
    checkPhoneNumber,
    checkEmail,
    constValues,
} from '@fnol/library/src/lib';
import { useApi } from '@fnol/library/src/hooks';
import { getCities, getRoles } from '../../rpc/actions';
import { personType, translations, legalPersonType } from '../../lib/consts';
import { useValidateIndividual, useValidateLegalEntity } from '../../lib/customHooks';
import { prepareCitiesList, prepareRisksList, prepareRolesList } from '../../lib/helpers';
import Individual from '../Individual';
import LegalIndividualSelector from '../LegalIndividualSelector';
import LegalEntity from '../LegalEntity';
import { Info } from '@mui/icons-material';
import { IndividualCleanup } from '../Individual/Individual';
import { LegalEntityCleanup } from '../LegalEntity/LegalEntity';

interface FormData {
    numberOfWaitingPeriodDays: string;
    cities: Array<GenericObject>;
    submitterType: legalPersonType;
    submitterLegalEntityContactName: string;
    submitterLegalEntityContactLastName: string;
    submitterLegalEntityContactEmail: string;
    submitterLegalEntityContactPhone: string;
    submitterIndividualPhone: string;
    submitterIndividualEmail: string;
    legalEntityIdFieldFormatError: boolean;
    VATFormatError: boolean;
    resetProcess: boolean;
    waitingPeriodAnswerGiven: boolean;
    submittedSuccessfully: boolean;
}

const SubmitterDetailScreen = ({ navigator }: ScreenProps) => {
    const [currentStep, { goForward, goBack }] = navigator;
    const { values, errors, touched, setFieldValue, setErrors, setTouched, isSubmitting, submitForm } =
        useFormikContext<FormData>();

    const submitterIndividualValidation = useValidateIndividual(personType.submitter);
    const submitterlegalEntityValidation = useValidateLegalEntity(personType.submitter);

    const saveCities = (res: GenericObject) => {
        setFieldValue('cities', prepareCitiesList(res.data));
    };

    const saveRoles = (res: GenericObject) => {
        setFieldValue('roles', prepareRolesList(res.data));
    };
    const { response: cities, fetchMethod: fetchCities } = useApi(() => getCities(), saveCities);
    const { response: roles, fetchMethod: fetchRoles } = useApi(() => getRoles(), saveRoles);

    useEffect(() => {
        if (values.submittedSuccessfully) {
            goForward();
        }
    }, [values.submittedSuccessfully]);

    const handleNext = (): void => {
        let valid = true;

        setTouched(
            {
                submitterType: true,
                ...submitterIndividualValidation.touched,
                ...submitterlegalEntityValidation.touched,
            },
            false,
        );

        let existingErrors: GenericObject = {};

        if (values.submitterType === legalPersonType.initialValue) {
            existingErrors.submitterType = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (values.submitterType === legalPersonType.legal) {
            if (!values.submitterLegalEntityContactName) {
                existingErrors.submitterLegalEntityContactName = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.submitterLegalEntityContactLastName) {
                existingErrors.submitterLegalEntityContactLastName = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (
                !values.submitterLegalEntityContactEmail &&
                !hasValueAndIsSalesForce(values.submitterLegalEntityContactPhone)
            ) {
                existingErrors.submitterLegalEntityContactEmail = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (values.submitterLegalEntityContactEmail && !checkEmail(values.submitterLegalEntityContactEmail)) {
                existingErrors.submitterLegalEntityContactEmail = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
                valid = false;
            }

            if (
                (!values.submitterLegalEntityContactPhone || values.submitterLegalEntityContactPhone.length <= 4) &&
                !hasValueAndIsSalesForce(values.submitterLegalEntityContactEmail)
            ) {
                existingErrors.submitterLegalEntityContactPhone = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (
                (!checkIfSerbianMobPhoneNumber(values.submitterLegalEntityContactPhone) ||
                    !checkPhoneNumber(values.submitterLegalEntityContactPhone)) &&
                (!hasValueAndIsSalesForce(values.submitterLegalEntityContactEmail) ||
                    values.submitterLegalEntityContactPhone.length > 4)
            ) {
                existingErrors.submitterLegalEntityContactPhone =
                    translations.validationMessages.PHONE_NUMBER_FORMAT_SERBIAN;
                valid = false;
            }

            existingErrors = { ...existingErrors, ...submitterlegalEntityValidation.errors };
            if (!submitterlegalEntityValidation.isValid) valid = false;
        }

        if (values.submitterType === legalPersonType.individual) {
            existingErrors = { ...existingErrors, ...submitterIndividualValidation.errors };
            if (!submitterIndividualValidation.isValid) valid = false;
        }

        if (
            values.submitterType === legalPersonType.individual &&
            values.submitterIndividualPhone &&
            (!checkIfSerbianMobPhoneNumber(values.submitterIndividualPhone) ||
                !checkPhoneNumber(values.submitterIndividualPhone)) &&
            (!hasValueAndIsSalesForce(values.submitterIndividualEmail) || values.submitterIndividualPhone.length > 4)
        ) {
            existingErrors.submitterIndividualPhone = translations.validationMessages.PHONE_NUMBER_FORMAT_SERBIAN;
            valid = false;
        }

        setErrors(existingErrors);

        if (valid) {
            submitForm();
        }
    };
    const handleGoBack = (): void => {
        goBack();
    };

    useEffect(() => {
        fetchCities();
        fetchRoles();
        if (
            values.numberOfWaitingPeriodDays &&
            values.numberOfWaitingPeriodDays != '0' &&
            !values.waitingPeriodAnswerGiven
        ) {
            setFieldValue('waitingPeriodMessageOpen', true);
        }
    }, []);

    const handleChangeSubmitterType = () => {
        if (values.submitterType === legalPersonType.individual) {
            LegalEntityCleanup(personType.submitter, setFieldValue);

            setFieldValue('submitterLegalEntityContactName', '');
            setFieldValue('submitterLegalEntityContactLastName', '');
            setFieldValue('submitterLegalEntityContactEmail', '');
            setFieldValue('submitterLegalEntityContactPhone', constValues.dialCodeSerbia);
        } else {
            IndividualCleanup(personType.submitter, setFieldValue);
        }
    };

    useEffect(() => {
        handleChangeSubmitterType();
    }, [values.submitterType]);

    useEffect(() => {
        if (values.resetProcess) {
            goBack();
        }
    }, [values.resetProcess]);

    return (
        <StepWrapper>
            <StyledCardSection>
                <LegalIndividualSelector />

                {values.submitterType === legalPersonType.legal && (
                    <>
                        <LegalEntity legalEntityType={personType.submitter}></LegalEntity>

                        <Typography
                            style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '16px' }}
                            variant="caption"
                        >
                            {translations.legalInfo.CONTACT_INFO}
                        </Typography>
                        <FormControl>
                            <Field
                                name="submitterLegalEntityContactName"
                                label={translations.legalInfo.NAME}
                                as={TextField}
                                inputProps={{
                                    maxLength: nameMaxLength,
                                }}
                                error={
                                    !!(
                                        touched.submitterLegalEntityContactName &&
                                        errors.submitterLegalEntityContactName
                                    )
                                }
                                required
                            />
                            {touched.submitterLegalEntityContactName && errors.submitterLegalEntityContactName ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterLegalEntityContactName}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Field
                                name="submitterLegalEntityContactLastName"
                                label={translations.legalInfo.LAST_NAME}
                                as={TextField}
                                inputProps={{
                                    maxLength: surnameMaxLength,
                                }}
                                error={
                                    !!(
                                        touched.submitterLegalEntityContactLastName &&
                                        errors.submitterLegalEntityContactLastName
                                    )
                                }
                                required
                            />
                            {touched.submitterLegalEntityContactLastName &&
                            errors.submitterLegalEntityContactLastName ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterLegalEntityContactLastName}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>

                        {getIsSalesForce(getApplicationId()) && (
                            <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                                <Info color="primary" />
                                <Typography variant="body2" paddingTop={0.5}>
                                    {translations.validationMessages.CONTACT_REQUIRED}
                                </Typography>
                            </Stack>
                        )}

                        <FormControl>
                            <PhoneInputField
                                name="submitterLegalEntityContactPhone"
                                specialLabel={
                                    !hasValueAndIsSalesForce(values.submitterLegalEntityContactEmail)
                                        ? translations.personalInfo.MOBILE_STAR
                                        : translations.personalInfo.MOBILE
                                }
                                country={'rs'}
                                required={!hasValueAndIsSalesForce(values.submitterLegalEntityContactEmail)}
                            />
                        </FormControl>
                        <FormControl>
                            <Field
                                name="submitterLegalEntityContactEmail"
                                label={translations.legalInfo.CONTACT_EMAIL}
                                type="email"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={
                                    !!(
                                        touched.submitterLegalEntityContactEmail &&
                                        errors.submitterLegalEntityContactEmail
                                    )
                                }
                                required={!hasValueAndIsSalesForce(values.submitterLegalEntityContactPhone)}
                            />
                            {touched.submitterLegalEntityContactEmail && errors.submitterLegalEntityContactEmail ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterLegalEntityContactEmail}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}

                {values.submitterType === legalPersonType.individual && (
                    <Individual individualType={personType.submitter} />
                )}
            </StyledCardSection>
            {isSubmitting ? (
                <CircularProgress />
            ) : (
                <StepperControls
                    onNext={handleNext}
                    onNextLabel={common.NEXT}
                    onPrevious={handleGoBack}
                    onPreviousLabel={common.PREVIOUS}
                    idNext="submitterDetailScreenNext"
                    idPrevious="submitterDetailScreenPrevious"
                />
            )}
        </StepWrapper>
    );
};

export default SubmitterDetailScreen;
